'use client';

import {
  animate,
  useDragControls,
  useMotionValue,
  type MotionValue,
  type Transition,
} from 'motion/react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

import {
  calculateDownloadManagerDockTransform,
  getNearestDownloadManagerDockCorner,
  getDownloadManagerRelativePanelCenter,
  hasExceededDownloadManagerDragDistance,
  type DownloadManagerDockCorner,
  type DownloadManagerDockSafeInsets,
} from '@/lib/download-manager-docking';

const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';
const DOCK_MARGIN_PX = 16;
const DRAG_DISTANCE_THRESHOLD_PX = 6;
const DOCK_TRANSITION = {
  type: 'tween',
  duration: 0.18,
  ease: [0.22, 1, 0.36, 1],
} as const;
const RESIZE_DOCK_TRANSITION = {
  type: 'tween',
  duration: 0.4,
  ease: [0.22, 1, 0.36, 1],
} as const;

export function getDownloadManagerResizeDockTransition(
  isReady: boolean,
  prefersReducedMotion: boolean
): typeof RESIZE_DOCK_TRANSITION | null {
  if (!isReady || prefersReducedMotion) return null;
  return RESIZE_DOCK_TRANSITION;
}

export function isDownloadManagerDockReady(
  hasResolvedMedia: boolean,
  dragEnabled: boolean,
  hasSettledInitialDock: boolean
): boolean {
  return hasResolvedMedia && (!dragEnabled || hasSettledInitialDock);
}

export function createBrowserSafeAreaInsetsCache(
  readInsets: () => DownloadManagerDockSafeInsets | undefined
) {
  let cached: DownloadManagerDockSafeInsets | undefined;
  let hasCachedValue = false;
  return {
    read: () => {
      if (!hasCachedValue) {
        cached = readInsets();
        hasCachedValue = true;
      }
      return cached;
    },
    invalidate: () => {
      hasCachedValue = false;
    },
  };
}

type UseDownloadManagerDockingOptions = {
  dockCorner: DownloadManagerDockCorner;
  isExpanded: boolean;
  prefersReducedMotion: boolean;
  onDockCornerChange: (corner: DownloadManagerDockCorner) => void;
};

type DownloadManagerDocking = {
  boundaryRef: RefObject<HTMLDivElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  dragControls: ReturnType<typeof useDragControls>;
  dragEnabled: boolean;
  isReady: boolean;
  x: MotionValue<number>;
  y: MotionValue<number>;
  onDragEnd: () => void;
  onDragStart: () => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  shouldSuppressExpand: () => boolean;
  startDrag: (event: ReactPointerEvent<HTMLElement>) => void;
};

export function useDownloadManagerDocking({
  dockCorner,
  isExpanded,
  prefersReducedMotion,
  onDockCornerChange,
}: UseDownloadManagerDockingOptions): DownloadManagerDocking {
  const boundaryRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const dragControls = useDragControls();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [dragEnabled, setDragEnabled] = useState(false);
  const [hasResolvedMedia, setHasResolvedMedia] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const isReadyRef = useRef(false);
  const [safeAreaCache] = useState(() =>
    createBrowserSafeAreaInsetsCache(readBrowserSafeAreaInsets)
  );
  const cornerRef = useRef(dockCorner);
  const isDraggingRef = useRef(false);
  const suppressExpandRef = useRef(false);
  const pointerStartRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
  } | null>(null);
  const resetSuppressionFrameRef = useRef<number | null>(null);
  const animationsRef = useRef<Array<ReturnType<typeof animate>>>([]);

  const stopAnimations = useCallback(() => {
    for (const animation of animationsRef.current) animation.stop();
    animationsRef.current = [];
  }, []);

  const dockToCorner = useCallback(
    (
      corner: DownloadManagerDockCorner,
      shouldAnimate: boolean,
      transition: Transition = DOCK_TRANSITION,
      markReady = true
    ) => {
      const panel = panelRef.current;
      const boundary = boundaryRef.current;
      if (!panel || !boundary || typeof window === 'undefined') return;
      const boundaryBounds = boundary.getBoundingClientRect();

      const target = calculateDownloadManagerDockTransform(corner, {
        viewportWidth: boundaryBounds.width,
        viewportHeight: boundaryBounds.height,
        panelWidth: panel.offsetWidth,
        panelHeight: panel.offsetHeight,
        margin: DOCK_MARGIN_PX,
        safeInsets: safeAreaCache.read(),
      });

      stopAnimations();
      if (shouldAnimate && !prefersReducedMotion) {
        animationsRef.current = [
          animate(x, target.x, transition),
          animate(y, target.y, transition),
        ];
      } else {
        x.set(target.x);
        y.set(target.y);
      }
      if (markReady) {
        isReadyRef.current = true;
        setIsReady(true);
      }
    },
    [prefersReducedMotion, safeAreaCache, stopAnimations, x, y]
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const syncMedia = () => {
      if (!mediaQuery.matches) {
        isReadyRef.current = false;
        setIsReady(false);
      }
      setDragEnabled(mediaQuery.matches);
      setHasResolvedMedia(true);
    };

    syncMedia();
    mediaQuery.addEventListener('change', syncMedia);
    return () => mediaQuery.removeEventListener('change', syncMedia);
  }, []);

  useEffect(() => {
    if (!hasResolvedMedia) return;
    if (!dragEnabled) {
      stopAnimations();
      x.set(0);
      y.set(0);
      return;
    }

    const panel = panelRef.current;
    const boundary = boundaryRef.current;
    if (!panel || !boundary) return;
    let frameId: number | null = null;
    let settleFrameId: number | null = null;
    const cancelInitialSettle = () => {
      if (settleFrameId === null) return;
      window.cancelAnimationFrame(settleFrameId);
      settleFrameId = null;
    };
    const settleInitialDock = () => {
      settleFrameId = window.requestAnimationFrame(() => {
        settleFrameId = window.requestAnimationFrame(() => {
          settleFrameId = null;
          if (isDraggingRef.current) return;
          dockToCorner(cornerRef.current, false, RESIZE_DOCK_TRANSITION, true);
        });
      });
    };
    const scheduleDock = () => {
      if (isDraggingRef.current) return;
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      if (!isReadyRef.current) cancelInitialSettle();
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        if (!isReadyRef.current) {
          dockToCorner(cornerRef.current, false, RESIZE_DOCK_TRANSITION, false);
          settleInitialDock();
          return;
        }
        const resizeTransition = getDownloadManagerResizeDockTransition(
          isReadyRef.current,
          prefersReducedMotion
        );
        dockToCorner(
          cornerRef.current,
          resizeTransition !== null,
          resizeTransition ?? RESIZE_DOCK_TRANSITION
        );
      });
    };

    scheduleDock();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(scheduleDock);
    observer?.observe(panel);
    observer?.observe(boundary);
    const scheduleViewportDock = () => {
      safeAreaCache.invalidate();
      scheduleDock();
    };
    window.addEventListener('resize', scheduleViewportDock);
    window.addEventListener('orientationchange', scheduleViewportDock);
    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      cancelInitialSettle();
      observer?.disconnect();
      window.removeEventListener('resize', scheduleViewportDock);
      window.removeEventListener('orientationchange', scheduleViewportDock);
    };
  }, [
    dockToCorner,
    dragEnabled,
    hasResolvedMedia,
    isExpanded,
    prefersReducedMotion,
    safeAreaCache,
    stopAnimations,
    x,
    y,
  ]);

  useEffect(() => {
    if (cornerRef.current === dockCorner) return;
    cornerRef.current = dockCorner;
    if (dragEnabled && isReadyRef.current) dockToCorner(dockCorner, false);
  }, [dockCorner, dockToCorner, dragEnabled]);

  useEffect(
    () => () => {
      stopAnimations();
      if (resetSuppressionFrameRef.current !== null) {
        window.cancelAnimationFrame(resetSuppressionFrameRef.current);
      }
    },
    [stopAnimations]
  );

  const startDrag = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!dragEnabled) return;
      pointerStartRef.current = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      suppressExpandRef.current = false;
      dragControls.start(event, {
        distanceThreshold: DRAG_DISTANCE_THRESHOLD_PX,
      });
    },
    [dragControls, dragEnabled]
  );

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const start = pointerStartRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    if (
      hasExceededDownloadManagerDragDistance(
        { x: start.x, y: start.y },
        { x: event.clientX, y: event.clientY },
        DRAG_DISTANCE_THRESHOLD_PX
      )
    ) {
      suppressExpandRef.current = true;
    }
  }, []);

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (pointerStartRef.current?.pointerId !== event.pointerId) return;
    pointerStartRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const onDragStart = useCallback(() => {
    isDraggingRef.current = true;
    suppressExpandRef.current = true;
    stopAnimations();
  }, [stopAnimations]);

  const onDragEnd = useCallback(() => {
    isDraggingRef.current = false;
    pointerStartRef.current = null;
    const panel = panelRef.current;
    const boundary = boundaryRef.current;
    if (panel && boundary) {
      const bounds = panel.getBoundingClientRect();
      const boundaryBounds = boundary.getBoundingClientRect();
      const nextCorner = getNearestDownloadManagerDockCorner(
        getDownloadManagerRelativePanelCenter(bounds, boundaryBounds),
        { width: boundaryBounds.width, height: boundaryBounds.height }
      );
      cornerRef.current = nextCorner;
      onDockCornerChange(nextCorner);
      dockToCorner(nextCorner, true);
    }

    resetSuppressionFrameRef.current = window.requestAnimationFrame(() => {
      suppressExpandRef.current = false;
      resetSuppressionFrameRef.current = null;
    });
  }, [dockToCorner, onDockCornerChange]);

  const shouldSuppressExpand = useCallback(() => {
    const shouldSuppress = suppressExpandRef.current;
    suppressExpandRef.current = false;
    return shouldSuppress;
  }, []);

  return {
    boundaryRef,
    panelRef,
    dragControls,
    dragEnabled,
    isReady: isDownloadManagerDockReady(hasResolvedMedia, dragEnabled, isReady),
    x,
    y,
    onDragEnd,
    onDragStart,
    onPointerMove,
    onPointerUp,
    shouldSuppressExpand,
    startDrag,
  };
}

function readBrowserSafeAreaInsets() {
  if (typeof document === 'undefined') return undefined;

  const probe = document.createElement('div');
  probe.style.cssText = [
    'position:fixed',
    'visibility:hidden',
    'pointer-events:none',
    'padding-top:env(safe-area-inset-top, 0px)',
    'padding-right:env(safe-area-inset-right, 0px)',
    'padding-bottom:env(safe-area-inset-bottom, 0px)',
    'padding-left:env(safe-area-inset-left, 0px)',
  ].join(';');
  document.body.append(probe);
  const styles = window.getComputedStyle(probe);
  const safeInsets = {
    top: Number.parseFloat(styles.paddingTop) || 0,
    right: Number.parseFloat(styles.paddingRight) || 0,
    bottom: Number.parseFloat(styles.paddingBottom) || 0,
    left: Number.parseFloat(styles.paddingLeft) || 0,
  };
  probe.remove();
  return safeInsets;
}
